import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { OpportunityWithSubmissions, DealListFieldMapping } from '../types'

interface DealsTableProps {
  opportunities: OpportunityWithSubmissions[]
  fieldMappings: DealListFieldMapping[]
}

const DealsTable: React.FC<DealsTableProps> = ({ opportunities, fieldMappings }) => {
  const navigate = useNavigate()
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set())

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  const formatCurrency = (amount?: number): string => {
    if (!amount) return 'N/A'
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount)
  }

  const getStageColor = (stage: string): string => {
    const normalizedStage = stage.toLowerCase()
    
    switch (normalizedStage) {
      case 'closed won':
      case 'funded':
        return 'text-success'
      case 'closed lost':
      case 'rejected':
        return 'text-danger'
      case 'proposal/price quote':
      case 'negotiation/review':
        return 'text-warning'
      default:
        return 'text-primary'
    }
  }

  const toggleExpanded = (opportunityId: string) => {
    const newExpanded = new Set(expandedRows)
    if (newExpanded.has(opportunityId)) {
      newExpanded.delete(opportunityId)
    } else {
      newExpanded.add(opportunityId)
    }
    setExpandedRows(newExpanded)
  }

  const handleChatterClick = (opportunityId: string) => {
    navigate(`/chatter/${opportunityId}`, {
      state: { from: '/iso-dashboard' }
    })
  }

  const renderCellValue = (opportunity: OpportunityWithSubmissions, fieldName: string) => {
    switch (fieldName) {
      case 'businessLegalName':
        return opportunity.businessLegalName || opportunity.name
      case 'stageName':
        return (
          <span className={`${getStageColor(opportunity.stageName)}`} style={{ fontWeight: '400' }}>
            {opportunity.stageName}
          </span>
        )
      case 'createdDate':
        return formatDate(opportunity.createdDate)
      case 'closeDate':
        return opportunity.closeDate ? formatDate(opportunity.closeDate) : 'N/A'
      case 'amount':
        return opportunity.amount ? formatCurrency(opportunity.amount) : 'N/A'
      default:
        return 'N/A'
    }
  }

  // Sort field mappings by display order
  const sortedFieldMappings = [...fieldMappings]
    .filter(mapping => mapping.isVisible)
    .sort((a, b) => a.displayOrder - b.displayOrder)

  return (
    <div className="deals-table-container">
      <div className="table-responsive">
        <table className="table table-hover mb-0">
          <thead style={{ backgroundColor: '#495057' }}>
            <tr>
              {sortedFieldMappings.map((mapping) => (
                <th 
                  key={mapping.fieldName}
                  className="border-0"
                  style={{ 
                    fontWeight: '600',
                    fontSize: '0.875rem',
                    color: '#ffffff',
                    padding: '1rem 0.75rem'
                  }}
                >
                  {mapping.fieldLabel}
                </th>
              ))}
              <th 
                className="border-0 text-center"
                style={{ 
                  fontWeight: '600',
                  fontSize: '0.875rem',
                  color: '#ffffff',
                  padding: '1rem 0.75rem',
                  width: '120px'
                }}
              >
                Actions
              </th>
              <th 
                className="border-0 text-center"
                style={{ 
                  fontWeight: '600',
                  fontSize: '0.875rem',
                  color: '#ffffff',
                  padding: '1rem 0.75rem',
                  width: '140px'
                }}
              >
                Submissions
              </th>
            </tr>
          </thead>
          <tbody>
            {opportunities.map((opportunity, index) => (
              <React.Fragment key={opportunity.id}>
                {/* Main Row */}
                <tr 
                  style={{
                    backgroundColor: index % 2 === 0 ? '#ffffff' : '#f8f9fa'
                  }}
                >
                  {sortedFieldMappings.map((mapping) => (
                    <td 
                      key={mapping.fieldName}
                      className="border-0"
                      style={{ 
                        padding: '0.75rem',
                        fontSize: '0.875rem',
                        fontWeight: '400',
                        verticalAlign: 'middle'
                      }}
                    >
                      {renderCellValue(opportunity, mapping.fieldName)}
                    </td>
                  ))}
                  
                  {/* Actions Column */}
                  <td 
                    className="border-0 text-center"
                    style={{ 
                      padding: '0.75rem',
                      verticalAlign: 'middle'
                    }}
                  >
                    <button
                      className="btn btn-outline-info btn-sm"
                      onClick={() => handleChatterClick(opportunity.id)}
                      style={{ 
                        fontSize: '0.75rem',
                        fontWeight: '400',
                        padding: '0.25rem 0.5rem'
                      }}
                    >
                      Chat
                    </button>
                  </td>
                  
                  {/* Submissions Column */}
                  <td 
                    className="border-0 text-center"
                    style={{ 
                      padding: '0.75rem',
                      verticalAlign: 'middle'
                    }}
                  >
                    <button
                      className="btn btn-outline-primary btn-sm"
                      onClick={() => toggleExpanded(opportunity.id)}
                      disabled={opportunity.submissions.length === 0}
                      style={{ 
                        fontSize: '0.75rem',
                        fontWeight: '400',
                        padding: '0.25rem 0.5rem',
                        opacity: opportunity.submissions.length === 0 ? 0.6 : 1
                      }}
                    >
                      {opportunity.submissions.length} Submission{opportunity.submissions.length !== 1 ? 's' : ''}
                      <span 
                        style={{ 
                          marginLeft: '0.25rem',
                          fontSize: '0.7rem',
                          transform: expandedRows.has(opportunity.id) ? 'rotate(180deg)' : 'rotate(0deg)',
                          transition: 'transform 0.3s ease-in-out',
                          display: 'inline-block'
                        }}
                      >
                        ▼
                      </span>
                    </button>
                  </td>
                </tr>
                
                {/* Expanded Submissions Row */}
                {expandedRows.has(opportunity.id) && (
                  <tr 
                    style={{
                      backgroundColor: index % 2 === 0 ? '#e8f4f8' : '#d1ecf1',
                      borderLeft: '4px solid #007bff'
                    }}
                  >
                    <td 
                      colSpan={sortedFieldMappings.length + 2}
                      className="border-0"
                      style={{ 
                        padding: '1rem 0.75rem',
                        paddingLeft: '1.5rem'
                      }}
                    >
                      {opportunity.submissions.length > 0 ? (
                        <div className="submissions-details">
                          <div 
                            className="d-flex align-items-center mb-3"
                            style={{
                              borderBottom: '1px solid #007bff',
                              paddingBottom: '0.5rem'
                            }}
                          >
                            <div 
                              style={{
                                width: '8px',
                                height: '8px',
                                backgroundColor: '#007bff',
                                borderRadius: '50%',
                                marginRight: '0.5rem'
                              }}
                            ></div>
                            <small 
                              className="fw-bold"
                              style={{ 
                                color: '#007bff',
                                fontSize: '0.85rem',
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px'
                              }}
                            >
                              Submissions for {opportunity.businessLegalName || opportunity.name}
                            </small>
                          </div>
                          <div className="submissions-list">
                            {opportunity.submissions.map((submission, submissionIndex) => (
                              <div 
                                key={submission.id}
                                className="submission-item d-flex justify-content-between align-items-center py-2 px-3 mb-2"
                                style={{ 
                                  fontSize: '0.85rem',
                                  backgroundColor: '#ffffff',
                                  borderRadius: '6px',
                                  border: '1px solid #dee2e6',
                                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                                }}
                              >
                                <div className="d-flex align-items-center">
                                  <div 
                                    style={{
                                      width: '6px',
                                      height: '6px',
                                      backgroundColor: '#28a745',
                                      borderRadius: '50%',
                                      marginRight: '0.75rem'
                                    }}
                                  ></div>
                                  <span className="fw-medium">{submission.lenderName || 'Unknown Lender'}</span>
                                </div>
                                <div className="d-flex align-items-center gap-3">
                                  <span 
                                    className="badge"
                                    style={{
                                      backgroundColor: submission.status.toLowerCase().includes('approved') ? '#28a745' : 
                                                     submission.status.toLowerCase().includes('rejected') ? '#dc3545' : '#6c757d',
                                      fontSize: '0.75rem'
                                    }}
                                  >
                                    {submission.status}
                                  </span>
                                  <span className="text-muted" style={{ fontSize: '0.8rem' }}>
                                    {formatDate(submission.createdDate)}
                                  </span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ) : (
                        <div className="text-center py-3">
                          <div 
                            className="d-flex align-items-center justify-content-center mb-2"
                            style={{
                              borderBottom: '1px solid #007bff',
                              paddingBottom: '0.5rem'
                            }}
                          >
                            <div 
                              style={{
                                width: '8px',
                                height: '8px',
                                backgroundColor: '#007bff',
                                borderRadius: '50%',
                                marginRight: '0.5rem'
                              }}
                            ></div>
                            <small 
                              className="fw-bold"
                              style={{ 
                                color: '#007bff',
                                fontSize: '0.85rem',
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px'
                              }}
                            >
                              Submissions for {opportunity.businessLegalName || opportunity.name}
                            </small>
                          </div>
                          <div 
                            className="py-3 px-4"
                            style={{
                              backgroundColor: '#ffffff',
                              borderRadius: '6px',
                              border: '1px solid #dee2e6'
                            }}
                          >
                            <div className="text-muted mb-2">
                              <i className="bi bi-inbox" style={{ fontSize: '1.5rem' }}></i>
                            </div>
                            <small className="text-muted">No submissions yet for this opportunity</small>
                          </div>
                        </div>
                      )}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export default DealsTable
